import {
  TOrderTnAStepDTO,
  TRESPONSE_GET_TNA_ORDER_TEMPLATE,
  TRESPONSE_TNA_DEFAULT_STEPS,
  TRESPONSE_TNA_TEMPLATE_DETAILS,
  TTnAStepDetailsDTO,
  TTnATeamsConfig,
} from '@monorepo/types';
import { ModifiersZTableModel } from '../../table.modifier';
import { TDropdownItem } from '../../../../form/model/form.model';
import { TTableCellTree, TTableInfo } from '../../table.type';
import moment from 'moment';

function getAllStages(tnaDefaultSteps: TRESPONSE_TNA_DEFAULT_STEPS['steps']) {
  const uniqStages = [...new Set(tnaDefaultSteps.map((_) => _.stage))];
  uniqStages.splice(0, 0, 'Other');
  return uniqStages;
}

function isOrderTnADetails(
  templateDetails: TRESPONSE_TNA_TEMPLATE_DETAILS | TRESPONSE_GET_TNA_ORDER_TEMPLATE,
): templateDetails is TRESPONSE_GET_TNA_ORDER_TEMPLATE {
  console.log('isOrderTnADetails called', templateDetails);
  return (<TRESPONSE_GET_TNA_ORDER_TEMPLATE>templateDetails).orderInfoDTO !== undefined;
}

function getTnaAvailableSteps(
  tnaDefaultSteps: TRESPONSE_TNA_DEFAULT_STEPS['steps'],
  tnaTemplateDetails: TRESPONSE_TNA_TEMPLATE_DETAILS['stepList'] | TRESPONSE_GET_TNA_ORDER_TEMPLATE['stepList'],
) {
  let steps: TRESPONSE_TNA_DEFAULT_STEPS['steps'] = [];
  if (tnaDefaultSteps !== null && tnaTemplateDetails !== null) {
    steps = tnaDefaultSteps.reduce((acc, defaultStep) => {
      if (!tnaTemplateDetails.find((tdsl) => tdsl.step === defaultStep.step)) {
        acc.push(defaultStep);
      }
      return acc;
    }, steps);
    const otherStep = {
      step: 'Other',
      stage: '',
      isCritical: true,
      isRequired: true,
    };
    steps.splice(0, 0, otherStep);
  }
  console.log('STEPS:', steps);
  return steps;
}

function getCriticalOptions() {
  return ['yes', 'no'].map((v) => ({ value: v }));
}

function formatCriticalTxt(criticalStatus: boolean) {
  return criticalStatus ? 'yes' : 'no';
}

export const TNA_TEMPLATE_DETAILS_TABLE_MODEL = {
  createModel: (
    templateDetails: TRESPONSE_TNA_TEMPLATE_DETAILS | TRESPONSE_GET_TNA_ORDER_TEMPLATE,
    defaultSteps: TRESPONSE_TNA_DEFAULT_STEPS,
    screenType: 'TNA_Details' | 'TNA_Orders',
  ) => {
    const availableSteps = getTnaAvailableSteps(defaultSteps['steps'], templateDetails['stepList']);

    const items = templateDetails['stepList'];

    return ModifiersZTableModel.createTableModel(
      items,
      {
        edit: {
          hasAddRow: true,
          hasDeleteRow: true,
          stickyType: screenType === 'TNA_Orders' ? 'RIGHT' : undefined,
        },
        headerConfig: [
          {
            type: 'TEXT_CELL',
            text: 'REORDER AFTER',
            stickyType: screenType === 'TNA_Orders' ? 'LEFT' : undefined,
            align: 'start',
            stickyPosition: 0,
            liftedInput: {
              input: {
                type: 'DROPDOWN',
                subType: 'number',
                options: items.map((_, index) => ({ value: index })),
                setValue: (v) => v.order,
                placeholder: 'Reorder after',
              },
              metadata: {
                keyName: 'order',
                isRequired: true,
              },
            },
          },
          {
            type: 'TEXT_CELL',
            text: 'STEPS',
            stickyType: screenType === 'TNA_Orders' ? 'LEFT' : undefined,
            align: 'left',
            stickyPosition: 1,
            liftedInput: {
              input: {
                type: 'DROPDOWN',
                options: availableSteps.map((_) => ({ value: _.step })),
                setValue: (v) => v.step,
                placeholder: 'Select step',
              },
              metadata: {
                keyName: 'step',
                isRequired: true,
                noEditsInTable: true,
              },
            },
          },
          {
            type: 'TEXT_CELL',
            text: 'STAGE',
            liftedInput: {
              input: {
                type: 'DROPDOWN',
                options: undefined,
                setValue: (v) => v.stage,
                placeholder: 'Select stage',
              },
              metadata: {
                keyName: 'stage',
                isRequired: true,
                isDisabled: true,
                noEditsInTable: true,
                dependsOn: {
                  keyName: 'step',
                  valuesCallback: (step) => {
                    if (step === 'Other') {
                      return {
                        updatedValues: {
                          type: 'DROPDOWN',
                          options: getAllStages(defaultSteps['steps']).map((stage) => ({ value: stage })),
                        },
                      };
                    } else {
                      const stage = availableSteps.find((alStep) => alStep.step === step)?.stage;
                      const options: TDropdownItem[] = [];
                      stage && options.push({ value: stage });
                      return {
                        updatedValues: {
                          type: 'DROPDOWN',
                          value: stage,
                          options,
                        },
                      };
                    }
                  },
                },
              },
            },
          },
          {
            type: 'TEXT_CELL',
            text: 'CRITICAL',
            liftedInput: {
              input: {
                type: 'DROPDOWN',
                options: getCriticalOptions(),
                setValue: (v) => v.critical,
                placeholder: 'Select yes or no',
              },
              metadata: {
                keyName: 'critical',
                isRequired: true,
                setNoEditsInTable: (v) => v.critical === 'yes',
                dependsOn: {
                  keyName: 'step',
                  valuesCallback: (step) => {
                    if (step === 'Other') {
                      return {
                        updatedValues: {
                          type: 'DROPDOWN',
                          options: getCriticalOptions(),
                        },
                      };
                    } else {
                      const critical = availableSteps.find((alStep) => alStep.step === step)?.isCritical;
                      let value = '';
                      const options: TDropdownItem[] = [];

                      if (critical !== undefined) {
                        value = formatCriticalTxt(critical);
                        options.push({ value });
                      }
                      return {
                        updatedValues: {
                          type: 'DROPDOWN',
                          value,
                          options,
                        },
                      };
                    }
                  },
                },
              },
            },
          },
          {
            type: 'TEXT_CELL',
            text: 'RESPONSIBLE TEAM',
            liftedInput: {
              input: {
                type: 'DROPDOWN',
                options: templateDetails.config.teams.map((team) => ({
                  value: team.name,
                })),
                setValue: (v) =>
                  templateDetails.config.teams.find((team) => team.name === v.responsibleTeam)?.name || undefined,
                placeholder: 'Select team',
              },
              metadata: {
                keyName: 'responsibleTeam',
                isRequired: screenType === 'TNA_Orders' ? true : undefined,
              },
            },
          },
          {
            type: 'TEXT_CELL',
            text: 'PRE-REQUISITE',
            maxWidthInPx: 200,
            liftedInput: {
              input: {
                type: 'DROPDOWN',
                isMulti: true,
                options: templateDetails.config.supportedPreReqSteps.map((preReq) => ({ value: preReq })),
                setValue: (v) => v.appliedPreReqSteps,
                setOptions: (v) => {
                  if (v.supportedPreReqSteps) return v.supportedPreReqSteps.map((preReq) => ({ value: preReq }));
                  return templateDetails.config.supportedPreReqSteps.map((preReq) => ({ value: preReq }));
                },
                placeholder: 'Select Pre-requisite',
              },
              metadata: {
                keyName: 'appliedPreReqSteps',
              },
            },
          },
          {
            type: 'TEXT_CELL',
            text: 'DURATION',
            liftedInput: {
              input: {
                type: 'INPUT',
                subType: 'number',
                placeholder: 'Enter days',
                setValue: (v) => v.buildAllowance,
              },
              metadata: {
                keyName: 'buildAllowance',
              },
            },
          },

          ...(screenType === 'TNA_Details' ? getTNATemplateDetailsHeaderConfig(templateDetails.config.teams) : []),
          ...(screenType === 'TNA_Orders' && isOrderTnADetails(templateDetails)
            ? getOrderTemplateHeaderConfig(
                templateDetails.config.teams,
                templateDetails.definitionState === 'TOT_DEFINED',
                templateDetails.completionState === 'TOT_COMPLETE',
              )
            : []),
        ],
      },
      (item) => {
        return {
          type: 'ROW',
          rowItem: item,
          cells: [
            {
              type: 'TEXT_CELL',
              text: item.order,
            },
            {
              type: 'TEXT_CELL',
              text: item.step,
              align: 'left',
              ...(item.isInProgress && screenType === 'TNA_Orders'
                ? {
                    marker: {
                      color: '',
                      iconId: 'IN_PROGRESS' as const,
                    },
                  }
                : {}),
            },
            {
              type: 'TEXT_CELL',
              text: item.stage,
            },
            {
              type: 'TEXT_CELL',
              text: item.critical,
            },
            {
              type: 'TEXT_CELL',
              text: item.responsibleTeam || '--',
            },
            {
              type: 'TEXT_CELL',
              text: item.appliedPreReqSteps.length ? item.appliedPreReqSteps[0] : '--',
              ...(item.appliedPreReqSteps.length > 1 && {
                tag: {
                  tagContent: `+${item.appliedPreReqSteps.length - 1}`,
                  tagTooltipContent: item.appliedPreReqSteps.slice(1).join(',\r\n'),
                  showTooltipEvent: 'hover',
                },
              }),
            },
            {
              type: 'TEXT_CELL',
              text: item.buildAllowance !== null ? item.buildAllowance + ' days' : '--',
            },
            ...(screenType === 'TNA_Details' ? getTNATemplateDetailsCells(item) : []),
            ...(screenType === 'TNA_Orders' ? getOrderTemplateCells(item) : []),
          ],
        };
      },
    );
  },
};

function getEmployeeNamesWithIds(teams: TTnATeamsConfig[], responsibleTeam: string | null) {
  const employees = teams.find((team) => team.name === responsibleTeam)?.employees;
  return (
    employees?.map((employee) => ({
      value: employee.username,
      displayName: `${employee.employeeName} (${employee.employeeId})`,
    })) || []
  );
}

function getTNATemplateDetailsCells(item: TTnAStepDetailsDTO): TTableCellTree[] {
  return [
    {
      type: 'TEXT_LIST_CELL',
      text: item.employees.length
        ? item.employees.map((employee) => `${employee.employeeName} (${employee.employeeId})`)
        : ['--'],
    },
  ];
}

function getOrderTemplateCells(item: TOrderTnAStepDTO): TTableCellTree[] {
  return [
    {
      type: 'TEXT_CELL' as const,
      text: item.isInProgress ? 'Yes' : '- -',
      align: 'start' as const,
      stickyPosition: 0,
    },

    {
      type: 'TEXT_LIST_CELL',
      text: item.employees.length
        ? item.employees.map((employee) => `${employee.employeeName} (${employee.employeeId})`)
        : ['--'],
    },
    {
      type: 'TEXT_CELL',
      text: item.targetDate ? moment(item.targetDate).format('D MMM') : '--',
    },
    {
      type: 'TEXT_CELL',
      text: item.expectedDate ? moment(item.expectedDate).format('D MMM') : '--',
    },
    {
      type: 'TEXT_CELL',
      text: item.dateFromPlan ? moment(item.dateFromPlan).format('D MMM') : '--',
    },
    {
      type: 'TEXT_CELL',
      text: item.completionDate ? moment(item.completionDate).format('D MMM') : '--',
    },
    {
      type: 'ACTION_LIST_CELL',
      title: 'Actions',
      actions: [
        ...(item.isInProgress
          ? []
          : [
              {
                type: 'STEP_IN_PROGRESS' as const,
                displayText: 'Mark as In-Progress',
              },
            ]),
        {
          type: 'ADDED_ADDITIONAL_DETAILS',
          displayText: 'Add/View Additional details',
        },
      ],
    },
  ];
}

function getTNATemplateDetailsHeaderConfig(teams: TTnATeamsConfig[]) {
  return [
    {
      type: 'TEXT_CELL',
      text: 'PEOPLE IN CHARGE',
      liftedInput: {
        input: {
          type: 'DROPDOWN',
          isMulti: true,
          setOptions: (value: TTnAStepDetailsDTO | TOrderTnAStepDTO) => {
            return getEmployeeNamesWithIds(teams, value.responsibleTeam);
          },
          setValue: (value: TTnAStepDetailsDTO | TOrderTnAStepDTO) =>
            value.employees ? value.employees.map((employee) => employee.username) : undefined,
          placeholder: 'Select person',
        },
        metadata: {
          keyName: 'employees',
          isRequired: false,
          dependsOn: {
            keyName: 'responsibleTeam',
            valuesCallback: (teamName: string) => {
              return {
                updatedValues: {
                  type: 'DROPDOWN',
                  value: undefined,
                  options: getEmployeeNamesWithIds(teams, teamName),
                },
              };
            },
          },
        },
      },
    },
  ] as TTableInfo['header']['cells'];
}

function getOrderTemplateHeaderConfig(
  teams: TTnATeamsConfig[],
  isRequired: boolean,
  isCompletionDateRequired: boolean,
) {
  return [
    {
      type: 'TEXT_CELL' as const,
      text: 'PROGRESS',
      align: 'start' as const,
      stickyPosition: 0,
    },
    {
      type: 'TEXT_CELL',
      text: 'PEOPLE IN CHARGE',
      liftedInput: {
        input: {
          type: 'DROPDOWN',
          isMulti: true,
          setOptions: (value: TOrderTnAStepDTO) => {
            return getEmployeeNamesWithIds(teams, value.responsibleTeam);
          },
          setValue: (value: TOrderTnAStepDTO) =>
            value.employees ? value.employees.map((employee) => employee.username) : undefined,
          placeholder: 'Select person',
        },
        metadata: {
          keyName: 'employees',
          isRequired: isRequired,
          dependsOn: {
            keyName: 'responsibleTeam',
            valuesCallback: (teamName: string) => {
              return {
                updatedValues: {
                  type: 'DROPDOWN',
                  value: undefined,
                  options: getEmployeeNamesWithIds(teams, teamName),
                },
              };
            },
          },
        },
      },
    },
    {
      type: 'TEXT_CELL',
      text: 'TARGET DATES',
      liftedInput: {
        input: {
          type: 'INPUT',
          subType: 'date',
          dateType: 'YYYY-MM-DD',
          placeholder: 'Select date',
          setValue: (value: TOrderTnAStepDTO) => (value.targetDate ? value.targetDate : undefined),
        },
        metadata: {
          keyName: 'targetDate',
          isRequired: isRequired,
          setNoEditsInTable: (value: TOrderTnAStepDTO) => value.completionDate,
        },
      },
    },
    {
      type: 'TEXT_CELL',
      text: 'EXPECTED DATES',
      liftedInput: {
        input: {
          type: 'INPUT',
          subType: 'date',
          dateType: 'YYYY-MM-DD',
          placeholder: 'Select date',
          setValue: (value: TOrderTnAStepDTO) => (value.expectedDate ? value.expectedDate : undefined),
        },
        metadata: {
          keyName: 'expectedDate',
          isRequired: false,
          setNoEditsInTable: (value: TOrderTnAStepDTO) => value.completionDate,
        },
      },
    },
    {
      type: 'TEXT_CELL',
      text: 'DATE FROM PLAN',
    },
    {
      type: 'TEXT_CELL',
      text: 'COMPLETION DATES',
      liftedInput: {
        input: {
          type: 'INPUT',
          subType: 'date',
          dateType: 'YYYY-MM-DD',
          placeholder: 'Select date',
          setValue: (value: TOrderTnAStepDTO) => (value.completionDate ? value.completionDate : undefined),
        },
        metadata: {
          keyName: 'completionDate',
          isRequired: isCompletionDateRequired,
          setNoEditsInTable: (value: TOrderTnAStepDTO) => {
            return !value.completionDateEditable;
          },
        },
      },
    },
    {
      type: 'TEXT_CELL',
      text: '',
      stickyType: 'RIGHT',
      widthInPx: 50,
    },
  ] as TTableInfo['header']['cells'];
}
